// CustomFormContainer.tsx

import React, {
  useEffect,
  useCallback,
  FormEvent,
  useState,
  useRef,
} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import "dayjs/locale/ja";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { CustomForm } from "./CustomForm";
import {
  ApplicationData,
  FormData,
  ApprovalActivity,
  Referent,
} from "../RecordDetail/ApplicationData";
import { axiosRequest } from "../../service/AxiosService";
import dayjs from "dayjs";
import { useUser } from "../../context/UserContext";
import { saveDraftData } from "../../service/RecordDetailService";

export type FormStructure = {
  id: string;
  title: string;
  subTitle: string;
  applicantList: ApplicantParam[];
  approvalActivityList: ApproverActivityParam[];
  section?: Section[];
};

/**
 * セクション（中分類）設定用のパラメータ型
 */
export type Section = {
  id: string;
  name: string;
  itemList: Item[];
};

/**
 * フォーム項目設定用のパラメータ型
 */
export type Item = {
  id: string;
  name: string;
  type: string;
  defaultValue?: string;
  selectedParam?: SelectParam[];
};

/**
 * セレクト項目のパラメータ型
 */
export type SelectParam = {
  id: string;
  value: string;
  label: string;
};

// 申請者情報のパラメータ
export type ApplicantParam = {
  id: string;
  lastName: string;
  firstName: string;
};

export type ApproverActivityParam = {
  id: string;
  order: number;
  activityName: string;
  approverList?: ApproverParam[];
};

// 承認者情報のパラメータ
export type ApproverParam = {
  id: string;
  lastName: string;
  firstName: string;
};

// 備考欄で貼り付ける画像データ
export type Image = {
  id: string;      // 一意の識別子
  base64: string; // 登録前プレビュー
}

// Base64をFileオブジェクトに変換
function extractBase64Images(html: string, files: File[], imgProfiles: Image[]) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const imgTags = doc.querySelectorAll("img");
  // const images: Image[] = [];

  imgTags.forEach((img, index) => {
    const src = img.getAttribute("src");
    if (src && src.startsWith("data:image")) {
      const id = `img-${index}-${Date.now()}`;
      const file = base64ToFile(src, `${id}.png`);

      imgProfiles.push({
        id,
        base64: src,
      });
      files.push(file);
    }
  }
  );

}

function base64ToFile(base64: string, filename: string): File {
  const arr = base64.split(",");
  const mime = arr[0].match(/:(.*?);/)?.[1] || "image/png";
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}

export const CustomFormContainer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const restoredData = location.state?.applicationData;
  const restoredFiles = location.state?.applicationFiles as
    | Record<number, File[]>
    | undefined;
  const copiedData = location.state?.copiedData as ApplicationData;

  const reApplicationFilesByFormId = location.state?.reApplicationFilesByFormId as
    Record<string, File[]> | undefined;
  const reApplicationEntryId = location.state?.reApplicationEntryId;

  const forms = useForm<ApplicationData>();
  const { control, getValues, setValue, reset, handleSubmit } = forms;
  const formDataField = useFieldArray({ control, name: "formEntryList" });

  const { id } = useParams<{ id: string }>();
  const { user, loading } = useUser(); // ユーザー情報を取得

  // 備考欄に貼り付けた画像を保持
  let imageFiles: File[] = [];
  let imgProfile: Image[] = [];

  // ファイルの一時保存用
  const uploadedFilesRef = useRef<Record<number, File[]>>({});

  // 一時保存確認ダイアログの表示状態
  const [showTempSaveDialog, setShowTempSaveDialog] = useState(false);


  // ファイル名の正規化：大小文字差や前後空白を吸収
  const normalizeName = (name: string) => name.trim().toLowerCase();

  // アプリケーション全体（全 formEntryList）で同名があるかを判定
  const isDuplicateAcrossAllItems = (getValuesFn: typeof getValues, candidateName: string) => {
    const normalized = normalizeName(candidateName);
    const all = getValuesFn("formEntryList") ?? [];
    for (const entry of all) {
      const val = entry?.value;
      const files = Array.isArray(val) ? (val as File[]) : [];
      if (files.some(f => normalizeName(f.name) === normalized)) {
        return true;
      }
    }
    return false;
  }
  // 初期表示処理
  useEffect(() => {
    const fetchApplicationData = async () => {
      //前画面から戻る際入力したデータを復元（navigate経由の場合）
      if (restoredData) {
        reset(restoredData);
        //前画面から戻る際アップロードしたファイルを復元
        if (restoredFiles) {
          Object.entries(restoredFiles).forEach(([indexStr, files]) => {
            const index = Number(indexStr);
            setValue(`formEntryList.${index}.value`, files);
            uploadedFilesRef.current[index] = files;
          });
        }
        return;
      }

      //既存の申請記録確認画面から入力画面に遷移する場合
      if (copiedData) {
        try {
          const response = await axiosRequest<ApplicationData>(
            "/customForm/getData",
            "POST",
            { data: { id: id } }
          );

          if (!response) {
            console.error("No response from /customForm/getData");
            return;
          }

          // バックエンドから取得したデータをベースデータとする
          const baseData: ApplicationData = {
            ...response,
            formEntryList: response.formEntryList || [],
          };

          // itemOrderでソート（未定義の場合は後ろに）
          const sortedBaseFormEntryList = [...baseData.formEntryList].sort(
            (a, b) => {
              const orderA = a.itemOrder ?? Number.MAX_SAFE_INTEGER;
              const orderB = b.itemOrder ?? Number.MAX_SAFE_INTEGER;
              return orderA - orderB;
            }
          );

          const mergedFormEntryList: FormData[] = sortedBaseFormEntryList.map(
            (baseEntry) => {
              const copiedEntry = copiedData.formEntryList.find(
                (entry) => entry.formId === baseEntry.formId
              );

              return {
                //入力内容(value)とリンクだけはコピー先のデータを使う
                ...baseEntry,
                value:
                  copiedEntry?.value !== undefined
                    ? copiedEntry.value
                    : baseEntry.value,
                formEntryId:
                  copiedEntry?.formEntryId !== undefined
                    ? copiedEntry.formEntryId
                    : baseEntry.formEntryId,
                formLinks: copiedEntry?.formLinks ?? baseEntry.formLinks,
              };
            }
          );

          // approvalActivityListのマージ（approverListのみコピー）
          const mergedApprovalActivityList: ApprovalActivity[] = (
            baseData.approvalActivityList ?? []
          ).map((baseActivity) => {
            const copiedActivity = copiedData.approvalActivityList?.find(
              (activity) =>
                activity.applicationActivityId ===
                baseActivity.applicationActivityId
            );

            return {
              ...baseActivity,
              approverList:
                copiedActivity?.approverList !== undefined
                  ? copiedActivity?.approverList
                  : baseActivity.approverList,
            };
          });

          // referentのマージ（userListのみコピー）
          const mergedReferent: Referent = {
            ...baseData.referent,
            userList: copiedData.referent?.userList ?? baseData.referent?.userList,
            profileList: copiedData.referent?.profileList ?? baseData.referent?.profileList,
            groupList: copiedData.referent?.groupList ?? baseData.referent?.groupList,
          };

          // データ全体をマージする（コピーデータで一部の項目に初期値を与える）
          const mergedData: ApplicationData = {
            ...baseData,
            formEntryList: mergedFormEntryList,
            applicant: copiedData.applicant ?? baseData.applicant,
            approvalActivityList: mergedApprovalActivityList,
            referent: mergedReferent,
            // 再申請時は applicationEntryId と number を保持する
            applicationEntryId: copiedData.applicationEntryId ?? baseData.applicationEntryId,
            number: copiedData.number ?? baseData.number,
          };

          // 入力画面の初期化
          reset(mergedData);

          if (reApplicationFilesByFormId) {
            mergedData.formEntryList.forEach((entry, idx) => {
              const files = entry.formId && entry.formId.trim() !== "" ? reApplicationFilesByFormId[entry.formId] : undefined;
              if (files && files.length) {
                // RHF に確実に更新を認識させるフラグを付ける
                setValue(`formEntryList.${idx}.value`, files, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                uploadedFilesRef.current[idx] = files;
              }
            });

          }
        } catch (error) {
          console.error("Error loading form structure:", error);
          navigate("/");
          return;
        }

        return;
      }

      if (!id || loading) {
        console.error("id is undefined or missing");
        return; // idがない場合は処理を中断
      }
      try {
        // const responseData = await getFormItems(id);
        const response = await axiosRequest<ApplicationData>(
        "/customForm/getData",
        "POST",
        { data: { id: id } }
      );
      if (response) {
        const formEntryList = Array.isArray(response.formEntryList)
          ? response.formEntryList
          : [];
        const applicationData: ApplicationData = {
          ...response,
          formEntryList: formEntryList
            .sort((a, b) => (a.itemOrder ?? 0) - (b.itemOrder ?? 0))
            .map((formData: FormData) => {
              if (formData.type === "date" && !formData.value) {
                // 日付型項目の初期値がnullの場合、明示的に今日日付を設定
                return { ...formData, value: dayjs().format("YYYY/MM/DD") };
              } else if (formData.type === "switch" && !formData.value) {
                // switch型項目の初期値がnullの場合、明示的にfalseを設定
                return { ...formData, value: false };
              } else {
                return { ...formData };
              }
            }),
        };

        //応答アクティビティの初期設定
        //予定されている作業者がいない場合、申請者をscheduledReporterとして登録する。
        applicationData.responseActivityList?.forEach((responseActivity) => {
          if (
            user &&
            (!responseActivity.scheduledReporterList ||
              responseActivity.scheduledReporterList.length === 0)
          ) {
            responseActivity.scheduledReporterList = [
              {
                id: user.id,
                lastName: user.lastName,
                firstName: user.firstName,
              },
            ];
          }
        });
        reset(applicationData); // 申請項目をセット

        //申請者の初期設定
        // applicantList から user.id に一致するものを初期値に設定
        if (user && response.applicantList) {
          const matchedApplicant = response.applicantList.find(
            (a) => a.id === user.id
          );
          if (matchedApplicant) {
            setValue("applicant", matchedApplicant);
          }
        }
        }
      } catch (error) {
        console.error("フォームデータ取得エラー:", error);
        navigate("/");
        return;
      }
    };
    fetchApplicationData();
  }, [
    id,
    reset,
    setValue,
    user,
    loading,
    restoredData,
    restoredFiles,
    copiedData,
    reApplicationFilesByFormId,
    reApplicationEntryId,
  ]);


  // 新: 既存配列に「すべての受け取ったファイル」を追加
  const onDrop = useCallback((acceptedFiles: File[], index: number) => {

    // 同一ドロップ内のファイル名重複チェック
    const namesInDrop = new Set<string>();
    const selfDup = acceptedFiles.filter(f => {
      const key = normalizeName(f.name);
      if (namesInDrop.has(key)) return true;
      namesInDrop.add(key);
      return false;
    });

    if (selfDup.length > 0) {
      // 任意: MUI Snackbar/AlertでもOK。ここでは簡易にalert。
      alert(`同一ドロップ内に同名ファイルが含まれています。\n重複: ${selfDup.map(d => d.name).join(", ")}`);
      return; // 追加拒否（または selfDup を除外して追加する運用も可）
    }

    // 追加候補の中で、申請全体で同名が既に存在するものを抽出
    const duplicates = acceptedFiles.filter(f => isDuplicateAcrossAllItems(getValues, f.name));

    if (duplicates.length > 0) {
      alert(
        `同一申請内で同名ファイルは添付できません。\n重複: ${duplicates.map(d => d.name).join(", ")}`
      );
      return; // 追加を拒否
    }

    const current = getValues(`formEntryList.${index}.value`);
    const prevFiles = Array.isArray(current) ? (current as File[]) : [];
    const nextFiles = [...prevFiles, ...acceptedFiles];

    setValue(`formEntryList.${index}.value`, nextFiles, {
      shouldDirty: true,
      shouldValidate: true,
    });
    uploadedFilesRef.current[index] = nextFiles;
  }, [getValues, setValue]);

  // 一時保存ダイアログを開く
  const handleTempSaveClick = (e: FormEvent) => {
    e.preventDefault();
    setShowTempSaveDialog(true);
  };

  // 一時保存実行処理（バリデーションをスキップ）
  const executeTempSave = async () => {
    // handleSubmitを使わず、直接データを取得（バリデーションをスキップ）
    const formEntryListFromValues = getValues("formEntryList");
    const safeFormEntryList = Array.isArray(formEntryListFromValues)
      ? formEntryListFromValues
      : [];
    const currentValues = getValues();
    const applicationData: ApplicationData = {
      ...currentValues,
      // applicationEntryId: フォームの値を優先、なければ reApplicationEntryId を使用
      applicationEntryId: currentValues.applicationEntryId ?? reApplicationEntryId,
      formEntryList: safeFormEntryList.map((formData: FormData) => {
        console.log(formData);
        switch (formData.type) {
          case "date":
            return {
              ...formData,
              value: dayjs(formData.value).format("YYYY/MM/DD"),
            };
          case "switch":
            return { ...formData, value: formData.value ? "true" : "false" };
          case "fileUpload":
            return {
              ...formData,
              value: Array.isArray(formData.value)
                ? JSON.stringify(
                  (formData.value as File[])
                    .map((file) => file.name)
                    .filter((name) => !!name && name.trim().length > 0)
                )
                : "[]",
            };
          case "linkList":
            return {
              ...formData,
              value: JSON.stringify(formData.formLinks ?? []),
              formLinks: formData.formLinks ?? [],
            };
          case "richText":
            extractBase64Images(formData.value, imageFiles, imgProfile);
            return {
              ...formData,
              value: formData.value ?? "",
              images: imgProfile,
            };
          default:
            return { ...formData, value: formData.value ?? "" };
        }
      }),
    };

    try {
      // 一時保存処理を実行
      const result = await saveDraftData(
        applicationData,
        uploadedFilesRef.current,
      );

      if (result) {
        // 成功時の処理: 完了画面に遷移
        navigate("/OperationCompleted", {
          state: {
            action: "一時保存",
            activityName: "一時保存",
          },
        });
      } else {
        // 失敗時の処理
        alert("一時保存に失敗しました");
      }
    } catch (error) {
      console.error("一時保存エラー:", error);
      alert("一時保存中にエラーが発生しました");
    }
  };

  // 申請ボタン押下処理
  const handleConfirm = (e: FormEvent) => {
    e.preventDefault(); // ページのリロードを防ぐ
    handleSubmit(() => {
      // フォーム項目の値は全てstring型で渡す
      const formEntryListFromValues = getValues("formEntryList");
      const safeFormEntryList = Array.isArray(formEntryListFromValues)
        ? formEntryListFromValues
        : [];
      // 申請確認画面に遷移する時点ではstatusは設定しない（確定ボタン押下時に設定）
      const currentValues = getValues();
      const applicationData: ApplicationData = {
        ...currentValues,
        // applicationEntryId: フォームの値を優先、なければ reApplicationEntryId を使用
        applicationEntryId: currentValues.applicationEntryId ?? reApplicationEntryId,
        // status は設定しない（undefined のまま）
        formEntryList: safeFormEntryList.map((formData: FormData) => {
          console.log(formData);
          switch (formData.type) {
            case "date":
              return {
                ...formData,
                value: dayjs(formData.value).format("YYYY/MM/DD"),
              };
            case "switch":
              return { ...formData, value: formData.value ? "true" : "false" };
            case "fileUpload":
              return {
                ...formData,
                value: Array.isArray(formData.value)
                  ? JSON.stringify(
                    (formData.value as File[])
                      .map((file) => file.name)
                      .filter((name) => !!name && name.trim().length > 0)
                  )
                  : "[]",
              };
            case "linkList":
              return {
                ...formData,
              value: JSON.stringify(formData.formLinks ?? []),
                formLinks: formData.formLinks ?? [],
              };
            case "richText":
              extractBase64Images(formData.value, imageFiles, imgProfile);
              return {
                ...formData,
                value: formData.value ?? "",
                images: imgProfile,
              };
            default:
              return { ...formData, value: formData.value ?? "" };
          }
        }),
      };

      if (formEntryListFromValues[0].formEntryId) {
        // RecordDetail 画面へ遷移する
        navigate(`/detail/`, {
          state: {
            applicationData,
            applicationFiles: uploadedFilesRef.current,
            from: "CustomForm",
            formEntryId: formEntryListFromValues[0].formEntryId,
            imageFiles,
          },
        });
      } else {
        //  RecordDetail 画面へ遷移する
        navigate(`/detail/`, {
          state: {
            applicationData,
            applicationFiles: uploadedFilesRef.current,
            from: "CustomForm",
            imageFiles,
          },
        });
      }
    })();
  };
  return (
    <>
      <FormProvider {...forms}>
        <form onSubmit={handleConfirm} noValidate>
          <CustomForm
            onDrop={onDrop}
            onTempSave={handleTempSaveClick}
          />
        </form>
      </FormProvider>

      {/* 一時保存確認ダイアログ */}
      <Dialog open={showTempSaveDialog} onClose={() => setShowTempSaveDialog(false)}>
        <DialogTitle>一時保存確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            入力内容を一時保存しますか？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTempSaveDialog(false)}>キャンセル</Button>
          <Button
            onClick={() => {
              executeTempSave();
              setShowTempSaveDialog(false);
            }}
            color="primary"
          >
            一時保存
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
